import { useEffect, useState } from "react";
import {
  IonContent,
  IonHeader,
  IonModal,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { Button, Colors, Icon } from "swing-components";

import { MarkdownContent } from "~components";
import type { DistrictsAnswerType } from "~onboarding/utils";
import { GET } from "~utils";

const INITIAL_DISTRICTS_COUNT = 5;

type DistrictApiResponse = {
  districts: string[];
  districtsPreview: string[];
};

type DistrictsAnswerProps = Omit<DistrictsAnswerType, "type"> & {
  /**
   * When the content is rendered, pass the `value` defined in the DistrictsAnswer type to the parent.
   */
  onChange: (value: string) => void;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

export function DistrictsAnswer(props: DistrictsAnswerProps) {
  const { markdownBefore, markdownAfter, isReadOnly, onChange, value } = props;
  const [isAllDistrictsModalOpen, setIsAllDistrictsModalOpen] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ["districts"],
    queryFn: () => GET("/api/sub/district") as Promise<{ data: unknown }>,
    select: (response) => response.data as DistrictApiResponse,
  });

  useEffect(() => {
    if (isReadOnly === false) {
      onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const districtsPreview: string[] = data?.districtsPreview ?? [];
  const districts: string[] = data?.districts ?? [];
  const visibleNames =
    districtsPreview.length >= INITIAL_DISTRICTS_COUNT
      ? districtsPreview.slice(0, INITIAL_DISTRICTS_COUNT)
      : districts.length > districtsPreview.length
        ? [...districtsPreview, ...districts.slice(districtsPreview.length)].slice(
            0,
            INITIAL_DISTRICTS_COUNT,
          )
        : districtsPreview;
  const hasMoreThanFive = districts.length > INITIAL_DISTRICTS_COUNT;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {markdownBefore && (
        <MarkdownContent data-testid="markdown" body={markdownBefore} css={{ width: "100%" }} />
      )}
      {isPending ? (
        <div
          aria-busy="true"
          aria-label="Loading districts"
          css={{
            height: 150,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <IonSkeletonText animated css={{ height: "100%", width: "100%", borderRadius: 8 }} />
        </div>
      ) : isError || districts.length === 0 ? null : (
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            paddingTop: 16,
            paddingBottom: hasMoreThanFive ? 10 : 16,
            border: `2px solid ${Colors.slate200}`,
            backgroundColor: Colors.white300,
            borderRadius: 8,
          }}
        >
          <ul css={{ marginTop: 0, marginBottom: 0 }}>
            {visibleNames.map((name: string) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          {hasMoreThanFive && (
            <div css={{ paddingLeft: 16, paddingRight: 16 }}>
              <Button
                type="tertiary"
                status={isReadOnly ? "disabled" : "enabled"}
                onClick={() => setIsAllDistrictsModalOpen(true)}
              >
                {`Show all ${districts.length} districts`}
              </Button>

              <IonModal
                isOpen={isAllDistrictsModalOpen}
                onDidDismiss={() => setIsAllDistrictsModalOpen(false)}
              >
                <IonHeader>
                  <IonToolbar>
                    <IonTitle>Districts</IonTitle>
                    <button
                      type="button"
                      slot="end"
                      onClick={() => setIsAllDistrictsModalOpen(false)}
                      css={{
                        background: "transparent",
                        border: "none",
                        padding: 8,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label="Close"
                    >
                      <Icon name="Close" color={Colors.blue500} />
                    </button>
                  </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                  <ul css={{ paddingInlineStart: 20, margin: 0 }}>
                    {districts.map((name: string) => (
                      <li key={name} css={{ marginBottom: 8 }}>
                        {name}
                      </li>
                    ))}
                  </ul>
                </IonContent>
              </IonModal>
            </div>
          )}
        </div>
      )}

      {markdownAfter && (
        <MarkdownContent data-testid="markdown" body={markdownAfter} css={{ width: "100%" }} />
      )}
    </div>
  );
}
